// @LastEditors: Jacky
// @LastEditTime: 2023-07-27 14:34:15
// @FilePath: /pool-interface/src/pages/WinePool/index.tsx
// Copyright (c) 2023 by Jacky, All Rights Reserved.
import { ChainId } from '@uniswap/sdk-core'
import { useWeb3React } from '@web3-react/core'
import BigNumber from 'bignumber.js'
import { PageWrapper, SwapWrapper } from 'components/swap/styleds'
import Text from 'components/Text'
import { getChainInfo } from 'constants/chainInfo'
import React, { useState } from 'react'
import { Send } from 'react-feather'
import styled from 'styled-components/macro'

import DepositModal from './components/DepositModal'
import WithdrawModal from './components/WithdrawModal'
import { masterChefV2, mjtAddress } from './constants'
import * as Styles from './styles'
import {
  addTokenToWallet,
  getPoolApr,
  useAllowanceAndApprove,
  useHarvest,
  useMjtBalance,
  usePendingReward,
  usePoolTotalStaked,
  useStake,
  useStakeBalance,
  useUnStake,
} from './utils'

const MJT_LOGO_URL = 'https://static-nft.mojitoswap.finance/token/mjt.png'

const Imgs = {
  AddIcon: require('./imgs/add.svg').default,
  MinusIcon: require('./imgs/minus.svg').default,
  MetaMask: require('./imgs/metamask.svg').default,
}

interface DoubleCurrencyLogoProps {
  margin?: string
  size?: number
}

const Wrapper = styled.div<{ margin?: string; sizeraw: number }>`
  position: relative;
  display: flex;
  flex-direction: row;
  overflow: visible;
  margin: ${({ margin }) => margin};
`

const MainLogo = styled.img`
  width: 56px;
  height: 56px;
  border-radius: 50%;
`

const SubLogo = styled.img`
  width: 36px;
  height: 36px;
  border: 3px solid #fff;
  position: absolute;
  background: #fff;
  bottom: 0px;
  right: -24px;
  border-radius: 50%;
`

const Button = styled.button<{ margin?: string; disabled?: boolean }>`
  border-radius: 30px;
  background: linear-gradient(112deg, #14d89d 0%, #0ac4dd 100%);
  width: 100%;
  height: 48px;
  outline: none;
  color: white;
  font-weight: bold;
  font-size: 17px;
  border: none;
  margin: ${({ margin }) => margin ?? '0px'};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`

export const BorderButton = styled.button<{ margin?: string; disabled?: boolean }>`
  border-radius: 30px;
  width: 120px;
  height: 40px;
  flex-shrink: 0;
  background: ${({ disabled }) => (disabled ? '#C9C9C9' : 'white')};
  border: 2px solid ${({ disabled }) => (disabled ? '#C9C9C9' : '#14D89D')};
  color: ${({ disabled }) => (disabled ? 'white' : '#14D89D')};
  margin: ${({ margin }) => margin ?? '0px'};
  font-weight: bold;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`

const Image = styled.img`
  width: 16px;
  height: 16px;
`

function FarmDoubleLogo({ size = 16, margin = '0px' }: DoubleCurrencyLogoProps) {
  return (
    <Wrapper sizeraw={size} margin={margin}>
      <MainLogo src={MJT_LOGO_URL} />
      <SubLogo src={MJT_LOGO_URL} />
    </Wrapper>
  )
}

export default function WinePools() {
  const [isDepositModalOpen, setIsDepositModalOpen] = useState(false)
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false)
  const { checkApprove, allowanceAmount, isApproving } = useAllowanceAndApprove()
  const { totalStake, updatePoolTotalStaked } = usePoolTotalStaked()
  const apr = getPoolApr(totalStake)
  const { stakeBalance, updateStakeBalance } = useStakeBalance()
  const { pendingReward, updatePendingReward } = usePendingReward()
  const { stake, isStaking } = useStake()
  const { harvest, isHarvesting } = useHarvest()
  const { unStake, isUnStaking } = useUnStake()
  const { mjtBalance, updateMjtBalance } = useMjtBalance()
  const { chainId } = useWeb3React()
  const info = chainId && chainId === ChainId.POLYGON ? getChainInfo(chainId) : undefined

  return (
    <PageWrapper>
      <SwapWrapper id="wine-pool-page">
        <Styles.Container>
          <Styles.Header>
            <Styles.Flex justify="space-between">
              <div>
                <Text fontSize="24px" bold>
                  Earn MJT
                </Text>
                <Text fontSize="16px" color="#666666" style={{ marginTop: '5px' }}>
                  Stake MJT
                </Text>
              </div>
              <FarmDoubleLogo margin="0 30px 0" />
            </Styles.Flex>
          </Styles.Header>
          <Styles.Divider />
          <Styles.Flex justify="space-between" align="center" margin="10px 0 0">
            <Text color="#666666">APR</Text>
            <Text color="#FF7008" fontSize="24px" bold>
              {apr && !!info ? new BigNumber(apr).toFixed(2, 0) : '-'}%
            </Text>
          </Styles.Flex>
          <Styles.Flex justify="space-between" align="center" margin="10px 0 0">
            <Text color="#666666">Deposit Fee</Text>
            <Text>0%</Text>
          </Styles.Flex>
          <Styles.Flex justify="space-between" align="center" margin="10px 0 0">
            <Text color="#666666">Staked Earning</Text>
          </Styles.Flex>
          {/* Need Approve */}
          {allowanceAmount.isEqualTo(new BigNumber(0)) || !info ? (
            <Button
              margin="16px 0 0"
              onClick={() => {
                if (isApproving || !info) return
                checkApprove()
              }}
              disabled={isApproving || !info}
            >
              Enable{isApproving ? '...' : ''}
            </Button>
          ) : (
            <div>
              {/* Action Area */}

              <Styles.Flex justify="space-between" margin="20px 0 0">
                <div>
                  <Text color="#14D89D">
                    MJT <span style={{ color: '#666666' }}>Earned</span>
                  </Text>
                  <Text bold fontSize="24px" color="#01142A" margin="5px 0 0">
                    {pendingReward.gt(0) && pendingReward.lt(0.01) ? '0.01' : pendingReward.toFixed(2, 1)}
                  </Text>
                </div>
                <Styles.Flex direct="column">
                  <BorderButton
                    disabled={isHarvesting}
                    onClick={async () => {
                      // if (isHarvesting || pendingReward.eq(0)) return
                      if (isHarvesting || !info) return
                      await harvest()
                      updatePendingReward()
                      updateMjtBalance()
                    }}
                  >
                    Harvest{isHarvesting ? '...' : ''}
                  </BorderButton>
                  <BorderButton
                    margin="15px 0 0"
                    // disabled={pendingReward.eq(0) || isStaking}
                    disabled={isStaking}
                    onClick={async () => {
                      // if (isStaking || pendingReward.eq(0)) return
                      if (isStaking || !info) return
                      await stake(pendingReward.toString())
                      updatePendingReward()
                      updateStakeBalance()
                      updatePoolTotalStaked()
                    }}
                  >
                    Compound{isStaking ? '...' : ''}
                  </BorderButton>
                </Styles.Flex>
              </Styles.Flex>
              <Styles.Flex justify="space-between" margin="20px 0 0">
                <div>
                  <Text color="#14D89D">
                    MJT <span style={{ color: '#666666' }}>Staked</span>
                  </Text>
                  <Text bold fontSize="24px" color="#01142A" margin="5px 0 0">
                    {stakeBalance.gt(0) && stakeBalance.lt(0.01) ? '0.01' : stakeBalance.toFixed(2, 0)}
                  </Text>
                </div>
                <Styles.Flex align="center">
                  <Styles.IconButton
                    disabled={stakeBalance.eq(0)}
                    onClick={() => {
                      if (stakeBalance.gt(0)) setIsWithdrawModalOpen(true)
                    }}
                    style={{ marginRight: '20px' }}
                  >
                    <Image src={Imgs.MinusIcon} />
                  </Styles.IconButton>
                  <Styles.IconButton onClick={() => setIsDepositModalOpen(true)}>
                    <Image src={Imgs.AddIcon} />
                  </Styles.IconButton>
                </Styles.Flex>
              </Styles.Flex>
            </div>
          )}

          <Styles.Divider margin="30px 0 16px -18px" />
          <Styles.Flex justify="space-between" margin="20px 0 0">
            <Text color="#666666">Total Staked</Text>
            <Text>{info ? totalStake.toFixed(2, 0) : '-'} MJT</Text>
          </Styles.Flex>
          <Styles.Flex
            justify="flex-end"
            margin="5px 0 0"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              window.open(`https://polygonscan.com/address/${mjtAddress}`, '_blank')
            }}
          >
            <Text color="#14D89D" margin="0 8px 0">
              View Token Info
            </Text>
            <Send width="16px" height="16px" color="#14D89D" />
          </Styles.Flex>
          <Styles.Flex
            justify="flex-end"
            margin="5px 0 0"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              window.open(`https://polygonscan.com/address/${masterChefV2}`, '_blank')
            }}
          >
            <Text color="#14D89D" margin="0 8px 0">
              View Contract
            </Text>
            <Send width="16px" height="16px" color="#14D89D" />
          </Styles.Flex>
          <Styles.Flex
            justify="flex-end"
            margin="5px 0 20px"
            style={{ cursor: 'pointer' }}
            onClick={() => addTokenToWallet()}
          >
            <Text color="#14D89D" margin="0 8px 0">
              Add MJT to MetaMask
            </Text>
            <Image src={Imgs.MetaMask} />
          </Styles.Flex>
        </Styles.Container>
      </SwapWrapper>
      <DepositModal
        isOpen={isDepositModalOpen}
        max={mjtBalance}
        onConfirm={async (amount) => {
          await stake(amount)
          updateMjtBalance()
        }}
        onDismiss={() => {
          setIsDepositModalOpen(false)
        }}
      />
      <WithdrawModal
        isOpen={isWithdrawModalOpen}
        max={stakeBalance}
        onConfirm={async (amount) => {
          await unStake(amount)
          updateMjtBalance()
        }}
        onDismiss={() => {
          setIsWithdrawModalOpen(false)
        }}
      />
    </PageWrapper>
  )
}
